import { FormEvent, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
    AlertCircle,
    BookOpen,
    Check,
    Copy,
    FileSearch,
    Loader2,
    Search
} from 'lucide-react';
import {
    SpecializedQueryResponse,
    querySpecializedKnowledgeBase
} from '../services/api/backend';

const EXAMPLE_QUESTIONS = [
    'O que é nepotismo na administração pública?',
    'Quais são os requisitos para contratação direta por inexigibilidade?',
    'Como os Tribunais de Contas analisam a segregação de funções?'
];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function findStringValue(record: Record<string, unknown>, keys: string[]): string | null {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === 'string' && value.trim()) {
            return value;
        }
    }

    return null;
}

function extractAnswer(response: SpecializedQueryResponse | null): string {
    if (!response) {
        return '';
    }

    const directAnswer = findStringValue(response, ['answer', 'response', 'result', 'text', 'content', 'output']);
    if (directAnswer) {
        return directAnswer;
    }

    const data = response.data;
    if (isRecord(data)) {
        const nestedAnswer = findStringValue(data, ['answer', 'response', 'result', 'text', 'content', 'output']);
        if (nestedAnswer) {
            return nestedAnswer;
        }
    }

    return JSON.stringify(response, null, 2);
}

function extractSources(response: SpecializedQueryResponse | null): unknown[] {
    if (!response) {
        return [];
    }

    const directSources = response.sources ?? response.source_documents ?? response.documents ?? response.results;
    if (Array.isArray(directSources)) {
        return directSources;
    }

    if (isRecord(response.data)) {
        const nestedSources = response.data.sources ?? response.data.source_documents ?? response.data.documents ?? response.data.results;
        if (Array.isArray(nestedSources)) {
            return nestedSources;
        }
    }

    return [];
}

function formatSourceTitle(source: unknown, index: number): string {
    if (!isRecord(source)) {
        return `Fonte ${index + 1}`;
    }

    const metadata = isRecord(source.metadata) ? source.metadata : {};
    return (
        findStringValue(source, ['title', 'source', 'document', 'file_name', 'filename', 'url']) ??
        findStringValue(metadata, ['title', 'source', 'document', 'file_name', 'filename', 'url']) ??
        `Fonte ${index + 1}`
    );
}

function formatSourceSnippet(source: unknown): string {
    if (typeof source === 'string') {
        return source;
    }

    if (!isRecord(source)) {
        return JSON.stringify(source, null, 2);
    }

    const metadata = isRecord(source.metadata) ? source.metadata : {};
    const snippet = findStringValue(source, ['content', 'text', 'page_content', 'snippet', 'excerpt']);
    if (snippet) {
        return snippet;
    }

    const metadataSnippet = findStringValue(metadata, ['content', 'text', 'page_content', 'snippet', 'excerpt']);
    if (metadataSnippet) {
        return metadataSnippet;
    }

    return JSON.stringify(source, null, 2);
}

function formatScore(source: unknown): string | null {
    if (!isRecord(source)) {
        return null;
    }

    const rawScore = source.score ?? source.similarity ?? source.relevance;
    if (typeof rawScore !== 'number') {
        return null;
    }

    return rawScore <= 1 ? `${Math.round(rawScore * 100)}%` : rawScore.toFixed(2);
}

export default function SpecializedQuery() {
    const [question, setQuestion] = useState('');
    const [response, setResponse] = useState<SpecializedQueryResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const answer = useMemo(() => extractAnswer(response), [response]);
    const sources = useMemo(() => extractSources(response), [response]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!question.trim()) {
            setError('Digite uma pergunta para consultar a base especializada.');
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            setResponse(null);

            const result = await querySpecializedKnowledgeBase({
                question: question.trim(),
                max_results: 20,
                include_sources: true,
            });

            setResponse(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Não foi possível concluir a consulta.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = async () => {
        if (!answer) {
            return;
        }

        await navigator.clipboard.writeText(answer);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-accent-400/10 px-3 py-1 text-sm font-medium text-accent-600 dark:text-accent-400">
                        <BookOpen className="h-4 w-4" />
                        Base RAG jurídica
                    </div>
                    <h1 className="mt-3 text-2xl font-bold text-navy-900 dark:text-navy-50">
                        Consulta especializada
                    </h1>
                    <p className="mt-1 max-w-3xl text-sm text-navy-500 dark:text-navy-400">
                        Consulte doutrina de direito administrativo e constitucional, além de jurisprudência dos Tribunais de Contas em direito administrativo.
                    </p>
                </div>
            </div>

            <form
                onSubmit={handleSubmit}
                className="rounded-xl border border-navy-200 bg-white p-5 shadow-lg dark:border-navy-700 dark:bg-navy-900"
            >
                <label htmlFor="specialized-question" className="form-label">
                    Pergunta
                </label>
                <textarea
                    id="specialized-question"
                    value={question}
                    onChange={(event) => setQuestion(event.target.value)}
                    rows={5}
                    className="form-input min-h-32 resize-y"
                    placeholder="Ex.: o que é nepotismo"
                    disabled={isLoading}
                />

                <div className="mt-4 flex flex-wrap gap-2">
                    {EXAMPLE_QUESTIONS.map((example) => (
                        <button
                            key={example}
                            type="button"
                            onClick={() => setQuestion(example)}
                            className="rounded-full border border-navy-200 px-3 py-1.5 text-sm text-navy-600 transition-colors duration-200 hover:border-accent-400 hover:text-accent-500 dark:border-navy-700 dark:text-navy-300 dark:hover:border-accent-400 dark:hover:text-accent-400"
                        >
                            {example}
                        </button>
                    ))}
                </div>

                <div className="mt-5 flex justify-end border-t border-navy-200 pt-5 dark:border-navy-700">
                    <button
                        type="submit"
                        disabled={isLoading || !question.trim()}
                        className="inline-flex items-center justify-center rounded-lg px-5 py-2.5 btn-accent disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        ) : (
                            <Search className="mr-2 h-5 w-5" />
                        )}
                        Consultar
                    </button>
                </div>
            </form>

            {error && (
                <div className="flex items-start gap-3 rounded-lg border border-error-500/30 bg-error-500/10 p-4 text-error-600 dark:text-error-400">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                    <p>{error}</p>
                </div>
            )}

            {!response && !isLoading && !error && (
                <div className="rounded-xl border border-dashed border-navy-300 bg-white/70 p-10 text-center dark:border-navy-700 dark:bg-navy-900/50">
                    <FileSearch className="mx-auto h-10 w-10 text-navy-400 dark:text-navy-500" />
                    <h2 className="mt-4 text-lg font-semibold text-navy-900 dark:text-navy-100">
                        Faça uma consulta jurídica especializada
                    </h2>
                    <p className="mx-auto mt-2 max-w-xl text-sm text-navy-500 dark:text-navy-400">
                        A resposta será gerada a partir da base treinada com doutrina e entendimentos aplicáveis ao direito administrativo.
                    </p>
                </div>
            )}

            {isLoading && (
                <div className="rounded-xl border border-navy-200 bg-white p-6 shadow-lg dark:border-navy-700 dark:bg-navy-900">
                    <div className="flex items-center gap-3 text-navy-600 dark:text-navy-300">
                        <Loader2 className="h-5 w-5 animate-spin text-accent-500" />
                        Consultando a base especializada...
                    </div>
                    <div className="mt-5 space-y-3">
                        <div className="skeleton h-4 w-full" />
                        <div className="skeleton h-4 w-11/12" />
                        <div className="skeleton h-4 w-4/5" />
                    </div>
                </div>
            )}

            {response && !isLoading && (
                <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
                    <section className="rounded-xl border border-navy-200 bg-white shadow-lg dark:border-navy-700 dark:bg-navy-900">
                        <div className="flex items-center justify-between border-b border-navy-200 px-5 py-4 dark:border-navy-700">
                            <h2 className="text-lg font-semibold text-navy-900 dark:text-navy-100">
                                Resposta
                            </h2>
                            <button
                                type="button"
                                onClick={handleCopy}
                                className="inline-flex items-center rounded-lg px-3 py-2 text-sm btn-secondary"
                            >
                                {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                                {copied ? 'Copiado' : 'Copiar'}
                            </button>
                        </div>
                        <div className="prose prose-navy max-w-none p-5 text-navy-800 dark:prose-invert dark:text-navy-100">
                            <ReactMarkdown>{answer}</ReactMarkdown>
                        </div>
                    </section>

                    <aside className="rounded-xl border border-navy-200 bg-white shadow-lg dark:border-navy-700 dark:bg-navy-900">
                        <div className="border-b border-navy-200 px-5 py-4 dark:border-navy-700">
                            <h2 className="text-lg font-semibold text-navy-900 dark:text-navy-100">
                                Fontes
                            </h2>
                            <p className="mt-1 text-sm text-navy-500 dark:text-navy-400">
                                {sources.length} referência{sources.length === 1 ? '' : 's'} retornada{sources.length === 1 ? '' : 's'}
                            </p>
                        </div>

                        <div className="max-h-[calc(100vh-16rem)] space-y-3 overflow-y-auto p-4">
                            {sources.length === 0 ? (
                                <p className="rounded-lg bg-navy-50 p-4 text-sm text-navy-500 dark:bg-navy-800 dark:text-navy-400">
                                    Nenhuma fonte foi retornada para esta consulta.
                                </p>
                            ) : (
                                sources.map((source, index) => {
                                    const score = formatScore(source);

                                    return (
                                        <article
                                            key={`${formatSourceTitle(source, index)}-${index}`}
                                            className="rounded-lg border border-navy-200 p-4 dark:border-navy-700"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <h3 className="text-sm font-semibold text-navy-900 dark:text-navy-100">
                                                    {formatSourceTitle(source, index)}
                                                </h3>
                                                {score && (
                                                    <span className="rounded-full bg-accent-400/10 px-2 py-0.5 text-xs font-medium text-accent-600 dark:text-accent-400">
                                                        {score}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-navy-600 dark:text-navy-300">
                                                {formatSourceSnippet(source)}
                                            </p>
                                        </article>
                                    );
                                })
                            )}
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}
